/**
 * Pure calculation functions for Australian property investment analysis.
 * No side effects, no API calls — safe to call from any component.
 */

// ── Land Tax (all 8 Australian states/territories) ───────────────────────────

export function calcLandTax(landValue, state) {
  const v = landValue || 0;
  switch (state) {
    case 'NSW':
      if (v <= 1075000) return 0;
      if (v <= 3385000) return 100 + (v - 1075000) * 0.016;
      return 37040 + (v - 3385000) * 0.02;

    case 'VIC':
      if (v <= 50000) return 0;
      if (v <= 100000) return (v - 50000) * 0.005;
      if (v <= 300000) return 250 + (v - 100000) * 0.008;
      if (v <= 600000) return 1850 + (v - 300000) * 0.0085;
      if (v <= 1000000) return 4400 + (v - 600000) * 0.011;
      if (v <= 1800000) return 8800 + (v - 1000000) * 0.0135;
      if (v <= 3000000) return 19600 + (v - 1800000) * 0.016;
      return 38800 + (v - 3000000) * 0.0225;

    case 'QLD':
      if (v <= 600000) return 0;
      if (v <= 1000000) return (v - 600000) * 0.01;
      if (v <= 3000000) return 4000 + (v - 1000000) * 0.015;
      return 34000 + (v - 3000000) * 0.0175;

    case 'WA':
      if (v <= 300000) return 0;
      if (v <= 1000000) return (v - 300000) * 0.0015;
      if (v <= 2200000) return 1050 + (v - 1000000) * 0.0065;
      return 8850 + (v - 2200000) * 0.017;

    case 'SA':
      if (v <= 530000) return 0;
      if (v <= 1100000) return (v - 530000) * 0.005;
      if (v <= 1900000) return 2850 + (v - 1100000) * 0.015;
      return 14850 + (v - 1900000) * 0.021;

    case 'TAS':
      if (v <= 25000) return 0;
      if (v <= 350000) return (v - 25000) * 0.0055;
      if (v <= 725000) return 1788 + (v - 350000) * 0.0085;
      if (v <= 1500000) return 4975 + (v - 725000) * 0.013;
      return 15050 + (v - 1500000) * 0.015;

    case 'ACT':
      if (v <= 150000) return 0;
      if (v <= 275000) return (v - 150000) * 0.003;
      if (v <= 500000) return 375 + (v - 275000) * 0.005;
      if (v <= 750000) return 1500 + (v - 500000) * 0.008;
      if (v <= 1000000) return 3500 + (v - 750000) * 0.013;
      return 6750 + (v - 1000000) * 0.016;

    case 'NT':
      return 0;

    default:
      return 0;
  }
}

// ── Negative Gearing Analysis ─────────────────────────────────────────────────

export function calcNegativeGearing(inputs) {
  const {
    purchasePrice, loanAmount, interestRate, weeklyRent,
    state, marginalTaxRate, expenses,
  } = inputs;

  const annualRent = (weeklyRent || 0) * 52;
  const interest = (loanAmount || 0) * ((interestRate || 0) / 100);
  const mgmtFee = annualRent * ((expenses.propertyMgmt || 0) / 100);
  const landValue = (purchasePrice || 0) * 0.4;
  const landTax = calcLandTax(landValue, state);

  const totalExpenses =
    interest +
    (expenses.rates || 0) +
    (expenses.insurance || 0) +
    mgmtFee +
    (expenses.repairs || 0) +
    (expenses.bodyCorp || 0) +
    landTax;

  const netPosition = annualRent - totalExpenses;
  const taxOffset = netPosition < 0 ? Math.abs(netPosition) * (marginalTaxRate || 0) : 0;
  const afterTaxWeeklyCost = (netPosition - taxOffset) / 52;

  return {
    annualRent,
    interest,
    mgmtFee,
    landTax,
    totalExpenses,
    netPosition,
    taxOffset,
    afterTaxWeeklyCost,
    gearing: netPosition > 0 ? 'positive' : netPosition < 0 ? 'negative' : 'neutral',
  };
}

// ── CGT Calculation ───────────────────────────────────────────────────────────

export function calcCGT(inputs, salePrice, yearsHeld) {
  const {
    purchasePrice, stampDuty, legalFees, improvements,
    depreciationClaimed, sellingCostPct, marginalTaxRate,
  } = inputs;

  const costBase =
    (purchasePrice || 0) +
    (stampDuty || 0) +
    (legalFees || 0) +
    (improvements || 0) -
    (depreciationClaimed || 0);

  const sellingCosts = (salePrice || 0) * ((sellingCostPct || 0) / 100);
  const grossCapitalGain = (salePrice || 0) - sellingCosts - costBase;

  // 50% CGT discount for assets held > 12 months
  const taxableGain = yearsHeld >= 1 && grossCapitalGain > 0
    ? grossCapitalGain * 0.5
    : grossCapitalGain;

  const cgtLiability = taxableGain > 0 ? taxableGain * (marginalTaxRate || 0) : 0;
  const netProceeds = (salePrice || 0) - sellingCosts - cgtLiability;

  return {
    costBase,
    sellingCosts,
    grossCapitalGain,
    taxableGain,
    cgtLiability,
    netProceeds,
    discountApplied: yearsHeld >= 1 && grossCapitalGain > 0,
  };
}

// ── ROI / CAGR Projection ─────────────────────────────────────────────────────

export function calcCAGR(initial, final, years) {
  if (!initial || initial <= 0 || years <= 0) return 0;
  return (Math.pow(final / initial, 1 / years) - 1) * 100;
}

export function projectROI(inputs) {
  const {
    purchasePrice, loanAmount, weeklyRent,
    annualCapitalGrowth, annualRentalGrowth, holdingYears,
  } = inputs;

  const initialEquity = (purchasePrice || 0) - (loanAmount || 0);
  const rows = [];
  let propertyValue = purchasePrice || 0;
  let currentWeeklyRent = weeklyRent || 0;

  for (let year = 1; year <= holdingYears; year++) {
    propertyValue *= 1 + (annualCapitalGrowth || 0) / 100;
    const ng = calcNegativeGearing({ ...inputs, weeklyRent: currentWeeklyRent });
    rows.push({
      year,
      propertyValue,
      annualRent: currentWeeklyRent * 52,
      netCashFlow: ng.netPosition,
      afterTaxCashFlow: ng.netPosition + ng.taxOffset,
      totalExpenses: ng.totalExpenses,
    });
    currentWeeklyRent *= 1 + (annualRentalGrowth || 0) / 100;
  }

  const lastRow = rows[rows.length - 1];
  const cgt = calcCGT(inputs, lastRow?.propertyValue || 0, holdingYears);
  const totalAfterTaxCashFlow = rows.reduce((s, r) => s + r.afterTaxCashFlow, 0);
  const capitalProfit = cgt.netProceeds - (purchasePrice || 0);
  const totalProfit = capitalProfit + totalAfterTaxCashFlow;
  const roi = initialEquity > 0 ? (totalProfit / initialEquity) * 100 : 0;
  const cagr = calcCAGR(initialEquity > 0 ? initialEquity : purchasePrice, (initialEquity > 0 ? initialEquity : purchasePrice) + totalProfit, holdingYears);
  const cashOnCash = initialEquity > 0 ? (rows[0]?.afterTaxCashFlow / initialEquity) * 100 : 0;

  return {
    rows,
    roi,
    cagr,
    totalAfterTaxCashFlow,
    capitalProfit,
    totalProfit,
    cgt,
    cashOnCash,
    finalValue: lastRow?.propertyValue || 0,
  };
}

// ── Scenario Simulator ────────────────────────────────────────────────────────

export function buildScenarios(baseInputs) {
  const bull = {
    ...baseInputs,
    annualCapitalGrowth: (baseInputs.annualCapitalGrowth || 3.5) + 3,
    annualRentalGrowth: (baseInputs.annualRentalGrowth || 3) + 1,
    interestRate: Math.max(0, (baseInputs.interestRate || 6.5) - 1),
  };
  const bear = {
    ...baseInputs,
    annualCapitalGrowth: Math.max(0, (baseInputs.annualCapitalGrowth || 3.5) - 2),
    annualRentalGrowth: Math.max(0, (baseInputs.annualRentalGrowth || 3) - 1),
    interestRate: (baseInputs.interestRate || 6.5) + 1.5,
  };

  return {
    bull: { label: 'Bull Case', color: '#15803d', ...projectROI(bull) },
    base: { label: 'Base Case', color: '#1a5baa', ...projectROI(baseInputs) },
    bear: { label: 'Bear Case', color: '#dc2626', ...projectROI(bear) },
  };
}

// ── Marginal tax rate brackets (2025-26) ─────────────────────────────────────

export const TAX_BRACKETS = [
  { label: '0% ($0 – $18,200)', rate: 0 },
  { label: '19% ($18,201 – $45,000)', rate: 0.19 },
  { label: '32.5% ($45,001 – $120,000)', rate: 0.325 },
  { label: '37% ($120,001 – $180,000)', rate: 0.37 },
  { label: '45% ($180,001+)', rate: 0.45 },
];

export const AU_STATES = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'NT', 'ACT'];
